import { ReactNode, useState, useCallback, memo, useMemo, useEffect, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, User, LogOut, FolderOpen, Shield, LayoutDashboard, Settings, Crown, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import { useSubscription } from '@/hooks/useSubscription';
import { useStreak } from '@/hooks/useStreak';
import { useTranslation } from '@/hooks/useTranslation';
import { ThemeToggle } from '@/components/ThemeToggle';
import StreakBadge from '@/components/StreakBadge';
import Logo from '@/components/Logo';
import ScrollToTop from '@/components/ScrollToTop';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import EmailVerificationBanner from '@/components/EmailVerificationBanner';

// Lazy load non-critical UI components
const OfflineIndicator = lazy(() => import('@/components/OfflineIndicator'));
const FloatingActionButton = lazy(() => import('@/components/FloatingActionButton'));
const CookieConsent = lazy(() => import('@/components/CookieConsent'));

// Awin Publisher ID for MasterTag
const AWIN_PUBLISHER_ID = import.meta.env.VITE_AWIN_PUBLISHER_ID;

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const { isTeam, isTeamMember } = useSubscription();
  const streak = useStreak();
  const { t } = useTranslation();

  // Memoize handlers to prevent unnecessary re-renders
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  }, [searchQuery, navigate]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/');
  }, [signOut, navigate]);

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);
  const toggleMobileMenu = useCallback(() => setIsMobileMenuOpen(prev => !prev), []);

  // Memoize current year to avoid recalculation
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  // Load Awin MasterTag script
  useEffect(() => {
    if (AWIN_PUBLISHER_ID && !document.getElementById('awin-mastertag')) {
      const script = document.createElement('script');
      script.id = 'awin-mastertag';
      script.src = `https://www.dwin1.com/${AWIN_PUBLISHER_ID}.js`;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  return <div className="min-h-screen flex flex-col bg-background">
      {/* Header - Apple-style minimal */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link to="/" className="hover:opacity-70 transition-opacity">
              <Logo />
            </Link>

            {/* Desktop Navigation - minimal */}
            <nav className="hidden md:flex items-center gap-1">
              <Link to="/categories" className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t('nav.categories')}
              </Link>
              <Link to="/pricing" className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                <Crown className="h-3.5 w-3.5" />
                Pricing
              </Link>
              {user && <Link to="/saved" className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.myChecklists')}
                </Link>}
            </nav>

            {/* Search Bar - cleaner */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center flex-1 max-w-sm mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="search" placeholder={t('nav.search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 bg-secondary border-0 rounded-full text-sm" />
              </div>
            </form>

            {/* Right side actions */}
            <div className="hidden md:flex items-center gap-1">
              {user && <StreakBadge currentStreak={streak.currentStreak} longestStreak={streak.longestStreak} todayCompleted={streak.todayCompleted} dailyGoal={streak.dailyGoal} goalProgress={streak.goalProgress} totalCompleted={streak.totalCompleted} compact />}
              <LanguageSwitcher />
              
              <ThemeToggle />
              {user ? <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full" aria-label="User menu">
                      <User className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 rounded-xl">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2">
                        <LayoutDashboard className="h-4 w-4" />
                        {t('nav.dashboard')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/saved" className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4" />
                        {t('nav.myChecklists')}
                      </Link>
                    </DropdownMenuItem>
                    {(isTeam || isTeamMember) && (
                      <DropdownMenuItem asChild>
                        <Link to="/team" className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Team
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        {t('nav.profile')}
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          {t('nav.admin')}
                        </Link>
                      </DropdownMenuItem>}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('nav.signOut')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu> : <Button size="sm" asChild>
                  <Link to="/auth">{t('nav.login')}</Link>
                </Button>}
            </div>

            {/* Mobile Menu Button */}
            <Button variant="ghost" size="icon" className="md:hidden" onClick={toggleMobileMenu}>
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && <div className="md:hidden py-4 border-t border-border/40 animate-fade-in-fast">
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="search" placeholder={t('nav.search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-secondary border-0 rounded-full" />
                </div>
              </form>
              <nav className="flex flex-col gap-1">
                <Link to="/categories" className="px-3 py-2.5 text-foreground hover:bg-secondary rounded-lg transition-colors" onClick={closeMobileMenu}>
                  {t('nav.categories')}
                </Link>
                <Link to="/pricing" className="px-3 py-2.5 text-foreground hover:bg-secondary rounded-lg transition-colors flex items-center gap-2" onClick={closeMobileMenu}>
                  <Crown className="h-4 w-4" />
                  Pricing
                </Link>
                {user ? <>
                    <Link to="/dashboard" className="px-3 py-2.5 text-foreground hover:bg-secondary rounded-lg transition-colors" onClick={closeMobileMenu}>
                      {t('nav.dashboard')}
                    </Link>
                    <Link to="/saved" className="px-3 py-2.5 text-foreground hover:bg-secondary rounded-lg transition-colors" onClick={closeMobileMenu}>
                      {t('nav.myChecklists')}
                    </Link>
                    <Link to="/profile" className="px-3 py-2.5 text-foreground hover:bg-secondary rounded-lg transition-colors" onClick={closeMobileMenu}>
                      {t('nav.profile')}
                    </Link>
                    <button onClick={() => { handleSignOut(); closeMobileMenu(); }} className="px-3 py-2.5 text-left text-destructive hover:bg-destructive/10 rounded-lg transition-colors">
                      {t('nav.signOut')}
                    </button>
                  </> : <Link to="/auth" className="px-3 py-2.5 text-foreground font-medium hover:bg-secondary rounded-lg transition-colors" onClick={closeMobileMenu}>
                    {t('nav.login')}
                  </Link>}
                <div className="mt-2 pt-2 border-t border-border/40 flex items-center justify-between px-3">
                  <span className="text-sm text-muted-foreground">{t('nav.theme')}</span>
                  <ThemeToggle />
                </div>
              </nav>
            </div>}
        </div>
      </header>

      {/* Email Verification Banner */}
      <EmailVerificationBanner />

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-secondary/30 border-t border-border/50 mt-auto">
        <div className="container mx-auto px-4 py-10 sm:py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="mb-4 inline-block hover:opacity-80 transition-opacity">
                <Logo />
              </Link>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('footer.tagline')}
              </p>
            </div>
            
            {/* Quick Links */}
            <div>
              <h2 className="font-semibold text-foreground mb-4 text-base">{t('footer.quickLinks')}</h2>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/categories" className="text-muted-foreground hover:text-foreground transition-colors">
                    {t('footer.allCategories')}
                  </Link>
                </li>
                <li>
                  <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
                    {t('footer.generateChecklist')}
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link to="/sitemap" className="text-muted-foreground hover:text-foreground transition-colors">
                    Sitemap
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Categories */}
            <div>
              <h2 className="font-semibold text-foreground mb-4 text-base">{t('footer.popularCategories')}</h2>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/category/home" className="text-muted-foreground hover:text-foreground transition-colors">
                    {t('footer.homeLife')}
                  </Link>
                </li>
                <li>
                  <Link to="/category/travel" className="text-muted-foreground hover:text-foreground transition-colors">
                    {t('footer.travel')}
                  </Link>
                </li>
                <li>
                  <Link to="/category/work" className="text-muted-foreground hover:text-foreground transition-colors">
                    {t('footer.workBusiness')}
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h2 className="font-semibold text-foreground mb-4 text-base">Support</h2>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/faq" className="text-muted-foreground hover:text-foreground transition-colors">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/accessibility" className="text-muted-foreground hover:text-foreground transition-colors">
                    Accessibility
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-border/50 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-muted-foreground text-xs">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <p>{t('footer.copyright', { year: currentYear })}</p>
              <a href="mailto:hello@nexihow.com" className="hover:text-foreground transition-colors font-medium">
                hello@nexihow.com
              </a>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/accessibility" className="hover:text-foreground transition-colors">Accessibility</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to top button */}
      <ScrollToTop />
      
      {/* Lazy-loaded non-critical components */}
      <Suspense fallback={null}>
        <OfflineIndicator />
      </Suspense>
      
      <Suspense fallback={null}>
        <FloatingActionButton />
      </Suspense>
      
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
    </div>;
};
export default Layout;